import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { database } from "../../database/firebase";

import "./lobby.css";
// import { syncJoinLobbyFromUrlSearch } from "../../utils/joinLobbyFromUrl";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";
const TOTAL_USER_COUNT_KEY = "mythFactTotalUserCount";
const LOBBY_PLAYER_COUNT = 2;

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

/** PIN digits must match the Firestore lobby document id (same normalization as host). */
function normalizeJoinPin(raw) {
  const t = String(raw ?? "").trim();
  if (!/^\d{1,4}$/.test(t)) return "";
  return t.padStart(4, "0");
}

export default function Lobby() {
  const navigate = useNavigate();
  const storedUserData = useMemo(() => readStoredUserData(), []);
  const sessionId = storedUserData.sessionId;
  const storedLobbyName = String(storedUserData.lobby || "").trim();
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [joining, setJoining] = useState(false);
  const [roomFull, setRoomFull] = useState(false);
  
  const handleCreate = async () => {
    try {
      sessionStorage.removeItem(CURRENT_LOBBY_KEY);
      sessionStorage.setItem(TOTAL_USER_COUNT_KEY, String(LOBBY_PLAYER_COUNT));
    } catch {
      // ignore storage write failures
    }
    navigate("/host", {
      state: { playerCount: LOBBY_PLAYER_COUNT, totalUserCount: LOBBY_PLAYER_COUNT },
    });
  };

  const handleNext = async () => {
    if (!pin.trim()) {
      setPinError("Please enter a PIN");
      return;
    }
    const userId =
      storedUserData.userId || storedUserData.userid || storedUserData.id;
    if (!sessionId || !userId) {
      setPinError("Missing session details. Please login again.");
      return;
    }

    const lobbyNameFromPin = normalizeJoinPin(pin.trim());
    if (!lobbyNameFromPin) {
      setPinError("Enter a valid 4-digit PIN");
      return;
    }

    setPinError("");
    setJoining(true);

    try {
      const lobbyDocRef = doc(
        database,
        `lobbies/${sessionId}/lobby/${lobbyNameFromPin}`
      );
      const lobbySnap = await getDoc(lobbyDocRef);
      if (!lobbySnap.exists()) {
        await Swal.fire({
          icon: "error",
          text: "Wrong lobby pin",
          confirmButtonText: "OK",
        });
        return;
      }

      const maxPlayers = LOBBY_PLAYER_COUNT;

      const usersRef = collection(
        database,
        `lobbies/${sessionId}/lobby/${lobbyNameFromPin}/users`
      );
      const usersSnap = await getDocs(usersRef);
      const currentUserDocRef = doc(
        database,
        `lobbies/${sessionId}/lobby/${lobbyNameFromPin}/users/${userId}`
      );
      const currentUserDocSnap = await getDoc(currentUserDocRef);
      const isAlreadyInLobby = currentUserDocSnap.exists();
      const isRoomFull =
        usersSnap.size >= maxPlayers && !isAlreadyInLobby;

      if (isRoomFull) {
        await Swal.fire({
          icon: "warning",
          text: "Room is full",
          confirmButtonText: "OK",
        });
        return;
      }

      try {
        sessionStorage.setItem(CURRENT_LOBBY_KEY, lobbyNameFromPin);
        sessionStorage.setItem(TOTAL_USER_COUNT_KEY, String(maxPlayers));
      } catch {
        // ignore storage failures
      }
      try {
        const merged = { ...storedUserData, lobby: lobbyNameFromPin };
        const payload = JSON.stringify(merged);
        localStorage.setItem("userData", payload);
        sessionStorage.setItem("userData", payload);
      } catch {
        // ignore storage failures
      }

      const pinNum = Number.parseInt(lobbyNameFromPin, 10);
      navigate("/waiting", {
        state: {
          pin: pinNum,
          loginPin: pinNum,
          lobbyName: lobbyNameFromPin,
        },
      });
    } catch (firestoreError) {
      console.error("Lobby join failed:", firestoreError);
      await Swal.fire({
        icon: "error",
        text: "Could not join lobby. Try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setJoining(false);
    }
  };

  useEffect(() => {
    document.documentElement.classList.add("lobby-static-bg");
    document.body.classList.add("lobby-static-bg");
    return () => {
      document.documentElement.classList.remove("lobby-static-bg");
      document.body.classList.remove("lobby-static-bg");
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const checkLobbyCapacity = async () => {
      if (!sessionId || !storedLobbyName) {
        if (isMounted) setRoomFull(false);
        return;
      }
      try {
        const lobbyDocRef = doc(
          database,
          `lobbies/${sessionId}/lobby/${storedLobbyName}`
        );
        const lobbySnap = await getDoc(lobbyDocRef);
        if (!lobbySnap.exists()) {
          if (isMounted) setRoomFull(false);
          return;
        }

        const maxPlayers = LOBBY_PLAYER_COUNT;

        const currentUserId =
          storedUserData.userId || storedUserData.userid || storedUserData.id;
        const usersRef = collection(
          database,
          `lobbies/${sessionId}/lobby/${storedLobbyName}/users`
        );
        const usersSnap = await getDocs(usersRef);
        const isRoomFullByCount = usersSnap.size >= maxPlayers;

        const currentUserDocRef = currentUserId
          ? doc(
              database,
              `lobbies/${sessionId}/lobby/${storedLobbyName}/users/${currentUserId}`
            )
          : null;
        const currentUserDocSnap = currentUserDocRef
          ? await getDoc(currentUserDocRef)
          : null;
        const isSameUserAlreadyJoined = currentUserDocSnap
          ? currentUserDocSnap.exists()
          : false;

        const shouldBlockForFullRoom =
          isRoomFullByCount && !isSameUserAlreadyJoined;

        if (!isMounted) return;
        setRoomFull(shouldBlockForFullRoom);
        if (shouldBlockForFullRoom) {
          await Swal.fire({
            icon: "warning",
            text: "Room is full. You can't join this lobby. Create your own lobby.",
            confirmButtonText: "OK",
          });
          return;
        }

        const pinNum = Number.parseInt(String(storedLobbyName).trim(), 10);
        navigate("/waiting", {
          state: {
            pin: pinNum,
            loginPin: pinNum,
            lobbyName: storedLobbyName,
          },
        });
      } catch (firestoreError) {
        console.error("Failed to check lobby capacity:", firestoreError);
      }
    };

    checkLobbyCapacity();

    return () => {
      isMounted = false;
    };
  }, [sessionId, storedLobbyName, navigate]);

  return (
    <div className="lobby-page">
      <div className="lobby-decor" aria-hidden="true">
        <span className="lobby-blob lobby-blob--1" />
        <span className="lobby-blob lobby-blob--2" />
        <span className="lobby-blob lobby-blob--3" />
        <span className="lobby-blob lobby-blob--4" />
      </div>

      <h1 className="lobby-title">LOBBY</h1>

      <div className="lobby-cards">
        <section className="lobby-glassCard" aria-label="Create lobby">
          <h2 className="lobby-cardTitle">CREATE</h2>
          <div className="lobby-cardBody">
            <p className="lobby-modeHint">1v1 · 2 players</p>
            <button
              type="button"
              className="lobby-createBtn"
              onClick={handleCreate}
            >
              Create
            </button>
          </div>
        </section>

        <section className="lobby-glassCard" aria-label="Join lobby">
          <h2 className="lobby-cardTitle">JOIN</h2>
          <div className="lobby-cardBody lobby-cardBody--join">
            <div className="lobby-field lobby-field--flush">
              <input
                className="lobby-pinInput"
                type="text"
                placeholder="Enter PIN"
                value={pin}
                maxLength={4}
                onChange={(e) => {
                  setPin(e.target.value);
                  setPinError("");
                }}
              />
              {pinError && <p className="lobby-errorText">{pinError}</p>}
            </div>
          </div>
        </section>
      </div>

      <button
        type="button"
        className="lobby-nextBtn"
        onClick={handleNext}
        disabled={joining || roomFull}
      >
        {joining ? "Joining..." : "Next"}
      </button>
    </div>
  );
}
